<template>
  <v-snackbar
    v-model="showPrompt"
    location="top center"
    multi-line
    timeout="-1"
  >
    {{ $t('New update available') }}

    <template #actions>
      <v-btn
        class="mx-2"
        size="small"
        color="primary"
        variant="flat"
        @click="skipWaiting"
      >
        {{ $t('Update Now') }}
      </v-btn>
      <v-btn
        class="mx-2"
        size="small"
        variant="outlined"
        @click="dismissUpdate"
      >
        {{ $t('Update Later') }}
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'

const registration = ref<ServiceWorkerRegistration | null>(null)
const waitingWorker = ref<ServiceWorker | null>(null)
const installingWorker = ref<ServiceWorker | null>(null)
const activeWorker = ref<ServiceWorker | null>(null)
const showPrompt = ref(false)
const updating = ref(false)

const skipWaiting = () => {
  if (!('serviceWorker' in navigator)) {
    window.location.reload()
    return
  }

  if (waitingWorker.value) {
    // send the message to the waiting worker and show updating state
    waitingWorker.value.postMessage({ type: 'SKIP_WAITING' })
    showPrompt.value = false
    updating.value = true

    // Wait for the new service worker to take control (controllerchange)
    let handled = false
    const onControllerChange = () => {
      if (handled) return
      handled = true
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange
      )
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange
    )

    // Fallback: if controllerchange doesn't fire, reload after 5s
    setTimeout(() => {
      if (!handled) {
        handled = true
        navigator.serviceWorker.removeEventListener(
          'controllerchange',
          onControllerChange
        )
        window.location.reload()
      }
    }, 5000)
    return
  }

  // no waiting worker, just reload
  window.location.reload()
}

const dismissUpdate = () => {
  showPrompt.value = false
}

// Watch for changes in the waiting worker
watch(
  () => waitingWorker.value,
  () => {
    if (waitingWorker.value) {
      showPrompt.value = true
    }
  },
  { immediate: true }
)

onMounted(async () => {
  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) return

    registration.value = reg
    activeWorker.value = reg.active || null
    waitingWorker.value = reg.waiting || null
    installingWorker.value = reg.installing || null

    // Watch for changes from server
    reg.addEventListener('updatefound', () => {
      const intervalId = setInterval(() => {
        if (reg.waiting) {
          waitingWorker.value = reg.waiting
          clearInterval(intervalId)
        }
      }, 1000)
      registration.value = reg
      installingWorker.value = reg.installing
    })

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      activeWorker.value = navigator.serviceWorker.controller
      waitingWorker.value = null
      installingWorker.value = null
      showPrompt.value = false
    })
  }
})

onUnmounted(() => {
  registration.value = null
  waitingWorker.value = null
  installingWorker.value = null
  activeWorker.value = null
})
</script>
